import { useQuery } from '@tanstack/react-query'

import api from '@/services/api'

import { useLocalAuth } from './LocalAuthContext'
import { canEditModule, type PermissionHolder } from './modulePermission'

/** Grants nothing until the session's own answer arrives. One object, so a
 *  memo keyed on the holder does not recompute while it waits. */
const PENDING: PermissionHolder = { pending: true }

/**
 * This session's grants — from GET /auth/me, the same `effective_permissions`
 * payload `require_permission` enforces — for any module rule
 * (canViewModule / canEditModule, the dossier's tab scope).
 *
 * Local JWT sessions already cache that payload on `localUser`. Entra/MSAL
 * sessions do not populate LocalAuth, so an absent local profile is not
 * authority: the live /auth/me answer decides (that route accepts Azure
 * tokens the same way `get_current_user` does), and until it answers — or if
 * it cannot — the holder is `pending` and grants nothing.
 */
export function useSessionPermissions(): PermissionHolder {
  const { localUser } = useLocalAuth()
  const session = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const { data } = await api.get<PermissionHolder>('/auth/me')
      return data
    },
    enabled: localUser == null,
    staleTime: 30_000,
    retry: false,
  })
  return localUser ?? session.data ?? PENDING
}

/**
 * Whether this session may change a module. Read-only until the session's
 * grants are known (see useSessionPermissions).
 */
export function useCanEditModule(moduleId: string): boolean {
  return canEditModule(useSessionPermissions(), moduleId)
}
